import { CommerceService } from "@/services/commerce"

import { canAccessPurchasePrice, checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticleConfiguration } from "../../shared/utils/getArticleInformation"

export const querySkuListPrices = {
  async skuListPrices(parent, args, context, info) {
    const { items } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.PRICES,
      operation: Operations.READ,
    })

    const commerceApi = CommerceService.api()

    let skuKeys = items.map(({ sku }) => {
      if (!sku) return null

      return `"${sku}"`
    })

    skuKeys = skuKeys.filter((n) => n)

    skuKeys = skuKeys.join(",")

    const defaultListPriceKeys = [
      `"priceList1"`,
      `"priceList2"`,
      `"priceList3"`,
      `"priceList4"`,
      `"priceList5"`,
      `"purchasePrice"`,
      `"minimumSellingPrice"`,
    ].join(",")

    try {
      const listPriceChannels = (
        await commerceApi
          .channels()
          .get({
            queryArgs: {
              where: `key in (${defaultListPriceKeys})`,
            },
          })
          .execute()
      )?.body?.results

      if (listPriceChannels === 0) {
        return null
      }

      const channelIds = listPriceChannels.map((channel) => `"${channel.id}"`).join(",")

      const prices = (
        await commerceApi
          .standalonePrices()
          .get({
            queryArgs: {
              where: `sku in (${skuKeys}) and channel(id in (${channelIds})) or sku in (${skuKeys}) and channel is not defined`,
            },
          })
          .execute()
      )?.body?.results

      let [articlePurchasePrices, articleGrossSellingPrices] = await getArticlePurchasePrice(items)

      if (prices.length < 1) return null

      const hasPurchasePriceAccess = canAccessPurchasePrice(user.role)

      // purchase price is only available to users with explicit purchase-price access - EINZA-2248
      if (!hasPurchasePriceAccess) {
        articlePurchasePrices = null
      }

      const visiblePrices = hasPurchasePriceAccess
        ? prices
        : prices.filter((price) => !price?.key?.includes("purchasePrice"))

      return {
        items: visiblePrices,
        articlePurchasePrices,
        total: visiblePrices.length,
        articleGrossSellingPrices,
      }
    } catch (err) {
      console.warn(err)
      return null
    }
  },
}

/**
 * Get the article purchase price
 *
 * @param {array} items
 *
 * @returns {Promise<array>}
 */
const getArticlePurchasePrice = async (items) => {
  let articlePurchasePrices = await Promise.all(
    items?.map(async (item) => {
      const configuration = await getArticleConfiguration({ sku: item.sku })
      const purchasePrice = configuration?.articlePurchasePrice
      const suggestedSellingPriceGross = configuration?.articleSuggestedSellingPriceGross

      if (!purchasePrice) return null

      return {
        sku: item.sku,
        value: purchasePrice,
        suggestedSellingPriceGross: suggestedSellingPriceGross,
      }
    }),
  )

  articlePurchasePrices = articlePurchasePrices.filter((n) => n)

  const app = []
  const assp = []
  articlePurchasePrices.forEach((item) => {
    const obj1 = { sku: item.sku, value: item.value }
    const obj2 = { sku: item.sku, value: item.suggestedSellingPriceGross }

    app.push(obj1)
    assp.push(obj2)
  })

  return [app, assp]
}
